'use client';

import { useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowRight, Check, RotateCcw, X } from 'lucide-react';
import { answerCampaignQuizQuestion } from './campaignQuiz.api';
import {
  trackCampaignQuizAnswer,
  trackCampaignQuizComplete,
  trackCampaignQuizRestart,
  trackCampaignQuizStart,
  trackCampaignSignupClick,
} from './campaignQuiz.analytics';
import type { CampaignQuizQuestion } from './campaignQuiz.types';
import type { Locale } from '@/lib/i18n/locale';

type AnswerResult = {
  selectedOptionId: string;
  correctOptionId: string;
  correct: boolean;
  explanation: string | null;
};

interface CampaignQuizGameProps {
  slug: string;
  questions: CampaignQuizQuestion[];
  locale?: Locale;
  scoreTemplate?: string;
  previewToken?: string;
}

const UI_COPY = {
  en: {
    answerError: 'We could not check that answer. Please try again.',
    fullTime: 'Full time',
    youScored: 'You scored',
    playRanked: 'Play Ranked',
    playAgain: 'Play the quiz again',
    question: 'Question',
    of: 'of',
    correctCount: 'correct',
    correctAnswer: 'Correct answer',
    incorrectAnswer: 'Your answer was incorrect',
    checking: 'Checking your answer…',
    correctFeedback: 'Correct — well played!',
    incorrectFeedback: 'Not quite — the correct answer is highlighted.',
    score: 'See my score',
    next: 'Next question',
  },
  ka: {
    answerError: 'პასუხის შემოწმება ვერ მოხერხდა. სცადეთ თავიდან.',
    fullTime: 'მატჩი დასრულდა',
    youScored: 'თქვენი შედეგია',
    playRanked: 'რეიტინგული თამაში',
    playAgain: 'კიდევ ერთხელ თამაში',
    question: 'კითხვა',
    of: '/',
    correctCount: 'სწორი',
    correctAnswer: 'სწორი პასუხი',
    incorrectAnswer: 'თქვენი პასუხი არასწორი იყო',
    checking: 'პასუხი მოწმდება…',
    correctFeedback: 'სწორია — კარგად ითამაშეთ!',
    incorrectFeedback: 'სწორი პასუხი მონიშნულია.',
    score: 'შედეგის ნახვა',
    next: 'შემდეგი კითხვა',
  },
  es: {
    answerError: 'No pudimos comprobar esa respuesta. Inténtalo de nuevo.',
    fullTime: 'Final del partido',
    youScored: 'Has acertado',
    playRanked: 'Jugar clasificatoria',
    playAgain: 'Jugar de nuevo',
    question: 'Pregunta',
    of: 'de',
    correctCount: 'correctas',
    correctAnswer: 'Respuesta correcta',
    incorrectAnswer: 'Tu respuesta fue incorrecta',
    checking: 'Comprobando tu respuesta…',
    correctFeedback: '¡Correcto, bien jugado!',
    incorrectFeedback: 'No exactamente; la respuesta correcta está resaltada.',
    score: 'Ver mi puntuación',
    next: 'Siguiente pregunta',
  },
  tr: {
    answerError: 'Bu cevabı kontrol edemedik. Lütfen tekrar dene.',
    fullTime: 'Maç sonu',
    youScored: 'Skorun',
    playRanked: 'Dereceli oyna',
    playAgain: 'Quizi tekrar oyna',
    question: 'Soru',
    of: '/',
    correctCount: 'doğru',
    correctAnswer: 'Doğru cevap',
    incorrectAnswer: 'Cevabın yanlıştı',
    checking: 'Cevabın kontrol ediliyor…',
    correctFeedback: 'Doğru — iyi oynadın!',
    incorrectFeedback: 'Tam değil; doğru cevap vurgulandı.',
    score: 'Skorumu gör',
    next: 'Sonraki soru',
  },
  ru: {
    answerError: 'Не удалось проверить ответ. Попробуй ещё раз.',
    fullTime: 'Матч окончен',
    youScored: 'Ты набрал',
    playRanked: 'Играть в рейтинговом режиме',
    playAgain: 'Пройти викторину ещё раз',
    question: 'Вопрос',
    of: 'из',
    correctCount: 'правильных ответов',
    correctAnswer: 'Правильный ответ',
    incorrectAnswer: 'Ты ответил неправильно',
    checking: 'Проверяем твой ответ…',
    correctFeedback: 'Верно — отлично сыграно!',
    incorrectFeedback: 'Не совсем — правильный ответ подсвечен.',
    score: 'Посмотреть мой результат',
    next: 'Следующий вопрос',
  },
  pt: {
    answerError: 'Não foi possível conferir essa resposta. Tente novamente.',
    fullTime: 'Fim de jogo',
    youScored: 'Você fez',
    playRanked: 'Jogar no modo ranqueado',
    playAgain: 'Jogar de novo',
    question: 'Pergunta',
    of: 'de',
    correctCount: 'acertos',
    correctAnswer: 'Resposta correta',
    incorrectAnswer: 'Sua resposta está incorreta',
    checking: 'Conferindo sua resposta…',
    correctFeedback: 'Correto — mandou bem!',
    incorrectFeedback: 'Quase! A resposta correta está destacada.',
    score: 'Ver meu resultado',
    next: 'Próxima pergunta',
  },
  de: {
    answerError: 'Diese Antwort konnte nicht überprüft werden. Versuch es erneut.',
    fullTime: 'Spielende',
    youScored: 'Du hast',
    playRanked: 'Ranked spielen',
    playAgain: 'Quiz erneut spielen',
    question: 'Frage',
    of: 'von',
    correctCount: 'richtig',
    correctAnswer: 'Richtige Antwort',
    incorrectAnswer: 'Deine Antwort war falsch',
    checking: 'Deine Antwort wird geprüft…',
    correctFeedback: 'Richtig — gut gespielt!',
    incorrectFeedback: 'Nicht ganz — die richtige Antwort ist markiert.',
    score: 'Mein Ergebnis ansehen',
    next: 'Nächste Frage',
  },
} as const satisfies Record<Locale, Record<string, string>>;

/**
 * Career-path prompts often just spell out the same club sequence that is
 * rendered as chips below ("Liverpool ➔ LA Galaxy"). Showing both duplicates
 * the question, so the heading is kept for screen readers only in that case.
 * Prompts that carry real extra wording are always shown.
 */
const BARE_CAREER_PROMPTS = ['Guess the player', 'Угадай игрока', 'Угадайте игрока', 'Угадай футболиста', 'Adivinhe o jogador', 'Errate den Spieler'];

function isRedundantCareerPrompt(question: CampaignQuizQuestion): boolean {
  if (question.type !== 'career_path' || question.details.length === 0) return false;

  // Letters of any script, so Russian («Арсенал» ➔ «Ювентус») and Georgian prompts compare too.
  const normalize = (value: string) => value.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
  const prompt = normalize(question.prompt);
  if (!prompt) return false;

  // A bare "Guess the player" carries no information once chips are shown.
  if (BARE_CAREER_PROMPTS.some((bare) => prompt === normalize(bare))) return true;

  return prompt === normalize(question.details.join(''));
}

function answerClasses(input: {
  optionId: string;
  selectedOptionId?: string;
  correctOptionId?: string;
}) {
  const isSelected = input.optionId === input.selectedOptionId;
  const isCorrect = input.optionId === input.correctOptionId;

  if (isCorrect) {
    return 'border-transparent bg-brand-green text-white shadow-[0_1.76px_6.334px_1.32px_rgba(56,182,14,0.25)]';
  }
  if (isSelected && input.correctOptionId === undefined) {
    return 'border-brand-yellow bg-transparent text-white shadow-[0_0_6.334px_1.32px_rgba(255,229,0,0.25)]';
  }
  if (isSelected) {
    return 'border-brand-red bg-transparent text-brand-red-light shadow-[0_1.76px_6.334px_1.32px_rgba(251,49,1,0.25)]';
  }
  return 'border-brand-yellow bg-transparent text-white shadow-[0_0_6.334px_1.32px_rgba(255,229,0,0.25)] hover:bg-white/[0.025]';
}

export function CampaignQuizGame({
  slug,
  questions,
  locale = 'en',
  scoreTemplate = 'You scored {score}/{total} — sign up free to save your score and defend it in a ranked duel.',
  previewToken,
}: CampaignQuizGameProps) {
  const copy = UI_COPY[locale];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, AnswerResult>>({});
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [complete, setComplete] = useState(false);
  const startedRef = useRef(false);

  const currentQuestion = questions[currentIndex];
  const currentResult = currentQuestion ? answers[currentQuestion.id] : undefined;
  const score = useMemo(
    () => Object.values(answers).filter((answer) => answer.correct).length,
    [answers],
  );

  const submitAnswer = async (question: CampaignQuizQuestion, optionId: string) => {
    if (submitting || answers[question.id]) return;
    if (!startedRef.current) {
      startedRef.current = true;
      trackCampaignQuizStart(slug, questions.length);
    }

    setSelectedOptionId(optionId);
    setSubmitting(true);
    setError(null);

    try {
      const result = await answerCampaignQuizQuestion({
        slug,
        questionId: question.id,
        selectedOptionId: optionId,
        previewToken,
        locale,
      });
      setAnswers((current) => ({
        ...current,
        [question.id]: {
          selectedOptionId: optionId,
          correctOptionId: result.correct_option_id,
          correct: result.correct,
          explanation: result.explanation,
        },
      }));
      trackCampaignQuizAnswer({
        slug,
        questionId: question.id,
        questionType: question.type,
        position: question.position,
        difficulty: question.difficulty,
        correct: result.correct,
      });
    } catch {
      setSelectedOptionId(null);
      setError(copy.answerError);
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = () => {
    if (!currentResult) return;
    if (currentIndex === questions.length - 1) {
      setComplete(true);
      trackCampaignQuizComplete(slug, score, questions.length);
      return;
    }
    setCurrentIndex((index) => index + 1);
    setSelectedOptionId(null);
    setError(null);
  };

  const restart = () => {
    trackCampaignQuizRestart(slug, score);
    setCurrentIndex(0);
    setAnswers({});
    setSelectedOptionId(null);
    setError(null);
    setComplete(false);
    startedRef.current = false;
  };

  if (complete) {
    const ctaHref = `/${locale}?signup=1&source=${slug}-quiz`;
    const guestScoreCopy = scoreTemplate
      .replace('{score}', String(score))
      .replace('{total}', String(questions.length));

    return (
      <div
        className="overflow-hidden rounded-xl bg-brand-blue font-poppins"
        data-testid="campaign-quiz-score"
      >
        <div className="px-5 py-6 text-center sm:px-8">
          <Image
            src="/assets/brand/goal-ball-small.webp"
            alt=""
            aria-hidden
            width={64}
            height={64}
            className="mx-auto size-14 object-contain"
          />
          <p className="mt-4 text-xs font-bold uppercase tracking-[0.22em] text-white/70">
            {copy.fullTime}
          </p>
          <h2 className="mt-1 text-3xl font-black text-white sm:text-4xl">
            {copy.youScored} <span className="text-brand-yellow">{score}/{questions.length}</span>
          </h2>
        </div>

        <div className="px-5 py-7 text-center sm:px-10 sm:py-9">
          <p className="mx-auto max-w-xl text-base font-semibold leading-relaxed text-white/85 sm:text-lg">
            {guestScoreCopy}
          </p>
          <Link
            href={ctaHref}
            onClick={() => {
              trackCampaignSignupClick(slug, 'score', {
                score,
                totalQuestions: questions.length,
              });
            }}
            className="mt-6 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-brand-yellow px-6 text-base font-bold text-black transition-colors hover:bg-brand-yellow/90 sm:w-auto"
          >
            {copy.playRanked}
            <ArrowRight className="size-5" aria-hidden />
          </Link>
          <button
            type="button"
            onClick={restart}
            className="mx-auto mt-5 flex items-center gap-2 text-sm font-bold text-white/75 transition-colors hover:text-white"
          >
            <RotateCcw className="size-4" aria-hidden />
            {copy.playAgain}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full font-poppins"
      data-testid="campaign-quiz-game"
    >
      <noscript>
        <style>{`.campaign-question-panel[hidden]{display:block!important;margin-top:1rem}.campaign-question-panel button{pointer-events:none}`}</style>
      </noscript>

      <div className="flex items-stretch gap-2.5">
        <div className="flex h-12 min-w-0 flex-1 items-center justify-center rounded-2xl bg-brand-blue px-4 font-semibold text-white sm:h-14 sm:text-lg">
          {copy.question} {currentIndex + 1} {copy.of} {questions.length}
        </div>
        <div className="flex h-12 min-w-28 shrink-0 items-center justify-center rounded-2xl bg-brand-blue px-4 font-semibold text-white sm:h-14 sm:text-lg">
          {score} {copy.correctCount}
        </div>
      </div>

      <div>
        {questions.map((question, questionIndex) => {
          const result = answers[question.id];
          const isCurrent = questionIndex === currentIndex;
          const pendingSelection = isCurrent ? selectedOptionId : null;
          return (
            <section
              key={question.id}
              hidden={!isCurrent}
              className="campaign-question-panel"
              aria-labelledby={`campaign-question-${question.id}`}
            >
              <div className="mt-3 rounded-3xl bg-surface-page px-5 py-5 sm:px-7 sm:py-6">
                <div className="flex min-h-20 flex-col justify-center sm:min-h-24">
                  <h3
                    id={`campaign-question-${question.id}`}
                    className={`text-balance text-xl font-bold leading-snug text-white sm:text-2xl ${
                      isRedundantCareerPrompt(question) ? 'sr-only' : ''
                    }`}
                  >
                    {question.prompt}
                  </h3>
                  {question.details.length > 0 ? (
                    question.type === 'career_path' ? (
                      <ol className="mt-4 flex flex-wrap items-center gap-2">
                        {question.details.map((club, clubIndex) => (
                          <li key={`${club}-${clubIndex}`} className="flex items-center gap-2">
                            <span className="rounded-xl border-2 border-brand-yellow px-3 py-2 text-sm font-bold text-white shadow-[0_0_6.334px_1.32px_rgba(255,229,0,0.15)] sm:text-base">
                              {club}
                            </span>
                            {clubIndex < question.details.length - 1 ? (
                              <span className="text-xl text-white/35" aria-hidden>→</span>
                            ) : null}
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <ul className="mt-4 grid gap-2.5 text-[15px] font-semibold leading-relaxed text-white/90 sm:text-lg">
                        {question.details.map((detail) => (
                          <li key={detail} className="flex gap-2.5">
                            <span className="text-brand-cyan" aria-hidden>•</span>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    )
                  ) : null}
                </div>
                {question.image_url ? (
                  <div className="mt-4 flex justify-center">
                    <Image
                      src={question.image_url}
                      alt=""
                      width={220}
                      height={220}
                      sizes="(min-width: 640px) 220px, 180px"
                      className="h-36 w-auto object-contain sm:h-44"
                    />
                  </div>
                ) : null}
              </div>

              <div className="mt-2.5 grid grid-cols-2 items-stretch gap-2.5">
                {question.options.map((option) => {
                  const effectiveSelected = result?.selectedOptionId ?? pendingSelection ?? undefined;
                  const stateClasses = result
                    ? answerClasses({
                        optionId: option.id,
                        selectedOptionId: result.selectedOptionId,
                        correctOptionId: result.correctOptionId,
                      })
                    : answerClasses({
                        optionId: option.id,
                        selectedOptionId: effectiveSelected,
                      });

                  return (
                    <button
                      key={option.id}
                      type="button"
                      disabled={submitting || Boolean(result)}
                      aria-pressed={effectiveSelected === option.id}
                      onClick={() => void submitAnswer(question, option.id)}
                      className={`relative flex min-h-[68px] items-center justify-center overflow-hidden rounded-2xl border-2 px-4 py-3 text-center font-semibold uppercase transition-colors disabled:cursor-default sm:min-h-[86px] sm:px-5 ${stateClasses}`}
                    >
                      <span className="leading-[1.15] [overflow-wrap:anywhere]">{option.text}</span>
                      {result && option.id === result.correctOptionId ? (
                        <Check className="ml-auto size-5 shrink-0" aria-label={copy.correctAnswer} />
                      ) : null}
                      {result && option.id === result.selectedOptionId && !result.correct ? (
                        <X className="ml-auto size-5 shrink-0" aria-label={copy.incorrectAnswer} />
                      ) : null}
                    </button>
                  );
                })}
              </div>

              {submitting ? (
                <div className="mt-4 flex items-center justify-center gap-2 text-sm font-bold text-white/55">
                  <span className="size-2 animate-pulse rounded-full bg-brand-yellow" />
                  {copy.checking}
                </div>
              ) : null}

              {error ? (
                <div className="mt-4 rounded-2xl border border-brand-red/40 bg-brand-red/10 px-4 py-3 text-sm font-semibold text-brand-red-light">
                  {error}
                </div>
              ) : null}

              {result ? (
                <div className="mt-4 px-4 py-4">
                  <p className={`font-black ${result.correct ? 'text-brand-green-light' : 'text-brand-red-light'}`}>
                    {result.correct ? copy.correctFeedback : copy.incorrectFeedback}
                  </p>
                  {result.explanation ? (
                    <p className="mt-1 text-sm font-medium leading-relaxed text-white/65">
                      {result.explanation}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    onClick={goNext}
                    className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-brand-blue px-5 font-bold text-white transition-colors hover:bg-brand-blue/90 sm:w-auto"
                  >
                    {currentIndex === questions.length - 1 ? copy.score : copy.next}
                    <ArrowRight className="size-4" aria-hidden />
                  </button>
                </div>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}
